import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useT, useLang } from "@/lib/i18n";
import { corpusToSystemPrompt, searchCorpus } from "@/lib/chatbot-corpus";
import { LeadFormDialog, hasSubmittedForm } from "@/components/LeadFormDialog";

const CHAT_MODEL = import.meta.env.NEXT_PUBLIC_CHAT_MODEL || "gpt-4o-mini";
const CHAT_ENDPOINT = "/api/chat";

type Msg = { role: "user" | "assistant"; content: string };

export function Chatbot() {
  const t = useT();
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const initialMessage = useMemo(
    () => ({
      role: "assistant" as const,
      content: lang === "zh"
        ? "你好！我是 OranAI 助手。关于产品、解决方案或如何开始使用，欢迎随时提问。"
        : "Hi! I'm OranAI's assistant. Ask me anything about our products, solutions, or how to get started.",
    }),
    [lang],
  );

  const [messages, setMessages] = useState<Msg[]>([initialMessage]);

  useEffect(() => {
    setMessages([initialMessage]);
  }, [initialMessage]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  // Local fallback for when /api/chat is unreachable or the key isn't configured.
  // Looks up the last user message in the bilingual corpus and returns the
  // best-matched entry. Same behavior as the previous mock implementation.
  const fallbackFromCorpus = (msgs: Msg[]): string => {
    const lastUser = [...msgs].reverse().find((m) => m.role === "user");
    if (!lastUser) return "…";
    const hits = searchCorpus(lastUser.content, lang, 2);
    if (hits.length === 0) {
      return t(
        "I couldn't find that in our docs yet. Try asking about OranHub, OranSim, OranGen, OranMed, our solutions, case studies, or how to get started. You can also email hey@photog.art.",
        "我暂时在知识库里没找到相关内容。可以试试问 OranHub、OranSim、OranGen、OranMed、解决方案、案例或如何开始。也欢迎邮件 hey@photog.art。",
      );
    }
    return hits.map((h) => h.text[lang]).join("\n\n");
  };

  // Real call to tuzi. The vite dev proxy (and prod reverse proxy) rewrites
  // /api/chat → https://api.tu-zi.com/v1/chat/completions and injects the
  // Authorization: Bearer <TUZI_API_KEY> header server-side, so the key is
  // never shipped to the browser.
  //
  // tuzi response shape (extends OpenAI's by wrapping in `data`):
  //   { code: 0, message: "ok", data: { choices: [{ message: { content } }] } }
  const callTuzi = async (msgs: Msg[]): Promise<{ reply?: string; error?: string }> => {
    const systemPrompt = corpusToSystemPrompt(lang);
    const body = {
      model: CHAT_MODEL,
      messages: [
        { role: "system", content: systemPrompt },
        ...msgs.map((m) => ({ role: m.role, content: m.content })),
      ],
      temperature: 0.7,
      stream: false,
    };
    const res = await fetch(CHAT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // 401 / 403 typically means key missing or invalid — fall back to corpus
      // so the user still gets something useful instead of a blank UI.
      const text = await res.text().catch(() => "");
      console.warn("[chatbot] tuzi error", res.status, text);
      return { reply: fallbackFromCorpus(msgs) };
    }
    const data = await res.json();
    // tuzi error envelope: { code != 0, message: "..." }
    if (typeof data?.code !== "undefined" && data.code !== 0 && data.code !== "0") {
      console.warn("[chatbot] tuzi non-zero code", data);
      return { reply: fallbackFromCorpus(msgs) };
    }
    // Support both OpenAI shape and tuzi-wrapped shape just in case.
    const content =
      data?.data?.choices?.[0]?.message?.content ??
      data?.choices?.[0]?.message?.content ??
      "";
    if (!content) {
      console.warn("[chatbot] tuzi empty content", data);
      return { reply: fallbackFromCorpus(msgs) };
    }
    return { reply: content };
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const result = await callTuzi(next);
      if (result.error) {
        setMessages((m) => [...m, { role: "assistant", content: result.error! }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", content: result.reply || "…" }]);
      }
    } catch (e) {
      console.error(e);
      // Network / proxy down — still try to answer from the corpus locally
      // so the chatbot UI never goes silent.
      setMessages((m) => [...m, { role: "assistant", content: fallbackFromCorpus(next) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-v2 landing-v2-chatbot fixed bottom-6 right-6 z-[100]">
      {open && (
        <div className="mb-3 w-[360px] max-w-[calc(100vw-3rem)] h-[520px] max-h-[calc(100vh-7rem)] flex flex-col glass-strong nebula-border rounded-2xl overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: "hsl(var(--brand))", boxShadow: "var(--shadow-glow)" }}
              />
              <div>
                <p className="text-sm font-medium text-foreground">{t("OranAI Assistant", "OranAI 助手")}</p>
                <p className="text-[10px] text-muted-foreground">{t("AI-powered · usually replies instantly", "AI 驱动 · 即时回复")}</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-foreground text-background rounded-br-sm whitespace-pre-wrap"
                      : "bg-white/5 text-foreground border border-white/10 rounded-bl-sm chatbot-md"
                  }`}
                >
                  {m.role === "user" ? (
                    m.content
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // External links open in a new tab; internal markdown stays inline.
                        a: ({ href, children }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 brand-text hover:opacity-80 break-all"
                          >
                            {children}
                          </a>
                        ),
                        // Tight spacing so multi-paragraph replies stay compact in the bubble.
                        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc pl-4 mb-2 last:mb-0 space-y-1">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 last:mb-0 space-y-1">{children}</ol>,
                        li: ({ children }) => <li className="leading-snug">{children}</li>,
                        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                        code: ({ children }) => (
                          <code className="px-1 py-0.5 rounded bg-white/10 text-[12px]">{children}</code>
                        ),
                        h1: ({ children }) => <p className="font-semibold mb-1">{children}</p>,
                        h2: ({ children }) => <p className="font-semibold mb-1">{children}</p>,
                        h3: ({ children }) => <p className="font-semibold mb-1">{children}</p>,
                      }}
                    >
                      {m.content}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-sm px-3.5 py-2 text-muted-foreground">
                  <Loader2 size={14} className="animate-spin" />
                </div>
              </div>
            )}
          </div>

          {messages.length >= 3 && !formSubmitted && !hasSubmittedForm() && (
            <div className="px-4 pb-2">
              <button
                onClick={() => setFormOpen(true)}
                className="text-sm px-4 py-1.5 rounded-xl border border-[hsl(var(--brand))]/40 brand-text hover:bg-white/5 transition-colors"
              >
                {t("Fill out the form", "填写表单")}
              </button>
            </div>
          )}

          <div className="border-t border-border p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder={t("Ask anything…", "有什么想了解的…")}
                className="flex-1 resize-none bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-white/25 max-h-32"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                className="h-9 w-9 grid place-items-center rounded-xl text-background transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105"
                style={{ background: "hsl(var(--brand))", boxShadow: "var(--shadow-glow)" }}
                aria-label="Send"
              >
                <Send size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className="group relative h-14 w-14 rounded-full grid place-items-center text-background transition-transform hover:scale-110 border-none"
        style={{ background: "hsl(var(--brand))", boxShadow: "var(--shadow-glow)" }}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      <LeadFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmitted={() => setFormSubmitted(true)}
      />
    </div>
  );
}
